import React from 'react';
import { useQuery } from 'react-query';
import {
  Box,
  Paper,
  Typography,
  Grid,
  CircularProgress,
} from '@mui/material';
import {
  LineChart,
  Line,
  ComposedChart,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { api } from '../services/api';

// Color mapping for categories
const CATEGORY_COLORS = {
  'good call': '#22C55E',
  'good': '#22C55E',
  'legitimate': '#22C55E',
  'scammer': '#EF4444',
  'spam': '#EAB308',
};

const getCategoryColor = (categoryName) => {
  if (!categoryName) return '#8884d8';
  const normalized = String(categoryName).trim().toLowerCase();
  return CATEGORY_COLORS[normalized] || '#8884d8';
};

export default function Statistics() {
  const { data: stats, isLoading: isLoadingStats } = useQuery(
    'statistics',
    () => api.getCallStatistics('7d')
  );

  const { data: categoryStats, isLoading: isLoadingCategories } = useQuery(
    'categoryStatistics',
    () => api.getCategoryStatistics('7d')
  );

  const { data: trendStats, isLoading: isLoadingTrends } = useQuery(
    'callTrends',
    () => api.getCallTrends('7d')
  );

  const { data: hourlyStats, isLoading: isLoadingHourly } = useQuery(
    'callHourly',
    () => api.getCallsByHour('24h')
  );

  if (isLoadingStats || isLoadingCategories || isLoadingTrends || isLoadingHourly) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  // Calculate statistics from real data
  const totalCalls = stats?.total_calls || 0;
  const scammerCalls = stats?.scammer_calls || 0;
  const ai1Calls = stats?.ai1_calls || 0;
  const ai2Calls = stats?.ai2_calls || 0;
  const blockRate = totalCalls > 0 ? ((scammerCalls / totalCalls) * 100).toFixed(1) : 0;

  const formatDayLabel = (dateString) => {
    if (!dateString) return '';
    const date = new Date(`${dateString}T00:00:00Z`);
    return new Intl.DateTimeFormat('en-US', { weekday: 'short' }).format(date);
  };

  const callTrendData = (trendStats || []).map(row => ({
    date: formatDayLabel(row.date),
    total: row.total,
    scammer: row.scammer,
    ai1: row.ai1 || 0,
    ai2: row.ai2 || 0,
  }));

  const categoryData = categoryStats?.categories || [
    { name: 'Scammer', value: 0 },
    { name: 'Spam', value: 0 },
    { name: 'Good Call', value: 0 },
  ];
  const nonZeroCategoryData = categoryData.filter((entry) => Number(entry.value) > 0);
  const categoryChartData = nonZeroCategoryData.length > 0
    ? nonZeroCategoryData
    : [{ name: 'No Calls', value: 1 }];

  const hourlyData = (hourlyStats || []).map(row => ({
    hour: row.hourStart
      ? new Intl.DateTimeFormat('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date(row.hourStart))
      : row.hour,
    calls: row.calls
  }));

  return (
    <Box>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Call Trends (Last 7 Days)
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={callTrendData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="total" stroke="#22C55E" name="Total Calls" strokeWidth={2} />
                <Line type="monotone" dataKey="scammer" stroke="#EF4444" name="Scammer Calls" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              AI Route Usage (Last 7 Days)
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={callTrendData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="ai1" stackId="aiUsage" fill="#F59E0B" name="AI1 Scammer Check" />
                <Bar dataKey="ai2" stackId="aiUsage" fill="#2563EB" name="AI2 Translation" />
                <Line type="monotone" dataKey="total" stroke="#22C55E" name="Total Calls" strokeWidth={2} />
              </ComposedChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Scammer Categories
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={categoryChartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {categoryChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={getCategoryColor(entry.name)} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Calls by Hour (24h)
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={hourlyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="hour" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="calls" fill="#22C55E" name="Total Calls" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Summary
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="textSecondary">
                  Total Calls (7 days)
                </Typography>
                <Typography variant="h5">
                  {totalCalls}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="textSecondary">
                  Scammer Calls (7 days)
                </Typography>
                <Typography variant="h5" color="error">
                  {scammerCalls}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <Typography variant="body2" color="textSecondary">
                  AI1 Calls
                </Typography>
                <Typography variant="h5" color="secondary.main">
                  {ai1Calls}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <Typography variant="body2" color="textSecondary">
                  AI2 Calls
                </Typography>
                <Typography variant="h5" color="primary.main">
                  {ai2Calls}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <Typography variant="body2" color="textSecondary">
                  Block Rate
                </Typography>
                <Typography variant="h5" color="warning.main">
                  {blockRate}%
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
